import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ImageUpload,
  UserAvatar,
} from "@nymbl/nos2-rules";
import { useState } from "react";
import { toast } from "sonner";
import {
  setFileVisibility,
  useAvatarUrl,
  useSetFileVisibility,
  useUploadImage,
} from "@/hooks/use-files";
import { authClient, useSession } from "@/lib/auth-client";
import { publishAvatar, removeAvatar } from "@/lib/public-files";

/** `updateUser` refused the new image (Better Auth reports it as `{ error }`). */
class AvatarImageError extends Error {
  constructor(message: string | undefined) {
    super(message || "Failed to update your photo");
  }
}

/**
 * Reference usage of the upload feature AND of public files: upload an image
 * through the server pipeline, make it public (an avatar is meant to be seen),
 * then point the Better Auth `user.image` at its stable `/f/:id/:name` URL.
 * `UserAvatar` renders that URL directly, for anyone — no session, no per-view
 * signing. A REPLACED or removed photo is made private again, so it does not
 * stay reachable at its public URL. Everything else stays private by default.
 */
export function ProfileAvatarCard() {
  const { data: session, refetch } = useSession();
  const uploadImage = useUploadImage();
  const setVisibility = useSetFileVisibility();
  const [isSaving, setIsSaving] = useState(false);

  const user = session?.user;
  if (!user) return null;

  // Taking the old photo out of public view is a clean-up step after the
  // change has happened, so it gets its own message rather than an error.
  const makePrivate = (fileId: string) => setFileVisibility(fileId, "private");
  // Better Auth's client reports failure as `{ error }` rather than throwing; the
  // flow needs a throw to know the image did not move (and roll the new photo back).
  const setImage = async (image: string) => {
    const result = await authClient.updateUser({ image });
    if (result.error) throw new AvatarImageError(result.error.message);
  };
  // Read at submit time, never from this render: another tab may have changed it.
  const currentImage = async () =>
    (await authClient.getSession({ query: { disableCookieCache: true } })).data?.user.image ?? null;
  function reportRetire(result: { retireError: unknown }) {
    if (result.retireError) toast.error("Your previous photo could not be made private");
  }

  async function handleSelect(file: File) {
    setIsSaving(true);
    try {
      const result = await publishAvatar(file, {
        upload: (f) => uploadImage.mutateAsync(f),
        makePublic: (fileId) => setVisibility.mutateAsync({ fileId, visibility: "public" }),
        setImage,
        makePrivate,
        currentImage,
      });
      reportRetire(result);
      refetch();
    } catch (err) {
      // Upload and make-public toast through their own mutations; only the
      // image step needs one here.
      if (err instanceof AvatarImageError) toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemove() {
    setIsSaving(true);
    try {
      const result = await removeAvatar({
        setImage,
        makePrivate,
        currentImage,
      });
      reportRetire(result);
      refetch();
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    } finally {
      setIsSaving(false);
    }
  }

  const busy = isSaving || uploadImage.isPending || setVisibility.isPending;

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle>Profile photo</CardTitle>
        <CardDescription>
          Upload a photo — it's resized and converted to WebP before storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <UserAvatar
          name={user.name}
          image={user.image}
          useImageUrl={useAvatarUrl}
          className="h-20 w-20 text-lg"
        />
        <div className="flex-1 space-y-3">
          <ImageUpload onSelect={handleSelect} isUploading={busy} label="Upload profile photo" />
          {user.image ? (
            <Button variant="ghost" size="sm" onClick={handleRemove} disabled={busy}>
              Remove photo
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
